import MainLayout from "@/components/layout/admin/MainLayout";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import axios from "axios";
import Select2Multiple from 'v-select2-multiple-component';

export default {
  components: {
    MainLayout,
    Loading,
    Select2Multiple,
  },
  data() {
    return {
      isLoading: false,
      academicYearArr: [],
      searchData: {
        acd_year_id: "",
      },
      balanceList: [],
      editId: null,
      editBalance: 0,
      finalizeYearName: null,
    };
  },
  computed: {
    isFinalized() {
      if (!this.balanceList || this.balanceList.length === 0) return true;
      return this.balanceList.every(b => b.is_finalized);
    }
  },
  watch: {
    'searchData.acd_year_id'() {
      this.search();
    }
  },
  methods: {
    handleKeydown(event) {
      const key = event.key;
      const value = event.target.value;
      const decimalIndex = value.indexOf('.');
      const hasDecimal = decimalIndex !== -1;
      const cursorPos = event.target.selectionStart;
      const isSelecting = event.target.selectionEnd - cursorPos > 0;

      // Allow control keys
      if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'].includes(key)) {
        return;
      }

      // Allow negative sign only at the start and only once
      if (key === '-') {
        if (cursorPos !== 0 || value.includes('-') || isSelecting) {
          event.preventDefault();
        }
        return;
      }

      // Allow decimal point only once
      if (key === '.') {
        if (hasDecimal || (value.includes('-') && cursorPos === 0)) {
          event.preventDefault();
        }
        return;
      }

      // Allow only digits
      if (!/^[0-9]$/.test(key)) {
        event.preventDefault();
        return;
      }

      // Check decimal places limit
      if (hasDecimal) {
        const beforeCursor = value.substring(0, cursorPos);
        const afterCursor = value.substring(cursorPos);
        const newValue = beforeCursor + key + afterCursor;
        const newDecimalIndex = newValue.indexOf('.');
        const newDecimalPart = newValue.substring(newDecimalIndex + 1);

        if (newDecimalPart.length > 2) {
          event.preventDefault();
          return;
        }
      }

      // Prevent leading zeros - digits at position 1 when position 0 is a non-decimal 0
      if (/^[0-9]$/.test(key) && !isSelecting) {
        if (cursorPos === 1 && value[0] === '0' && value[1] !== '.') {
          event.preventDefault();
          return;
        }
        if (cursorPos === 2 && value[0] === '-' && value[1] === '0' && value[2] !== '.') {
          event.preventDefault();
          return;
        }
      }
    },
    formatNumber(value) {
      if (!value) return 0;
      return parseFloat(value).toLocaleString();
    },
    getAcademicYear() {
      axios.get("/cashflow/academic-years")
        .then((response) => {
          this.academicYearArr = response.data;
          this.searchData.acd_year_id = this.academicYearArr[0].id;
          this.search();
        })
        .catch((error) => {
          if (error.response && error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          console.error(error);
        });
    },
    reset() {
      this.searchData.acd_year_id = "";
      this.balanceList = [];
      this.editId = null;
    },
    search() {
      if (!this.searchData.acd_year_id) return;
      
      this.isLoading = true;
      this.editId = null;
      axios
        .get("/payment-method-balance/list", {
          params: { acd_year_id: this.searchData.acd_year_id }
        })
        .then((response) => {
          this.isLoading = false;
          if (response.data.success) {
            this.balanceList = response.data.list;
          }
        })
        .catch((error) => {
          this.isLoading = false;
          if (error.response && error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          console.error(error);
        });
    },
    startEdit(balance) {
      this.editId = balance.id;
      this.editBalance = balance.opening_balance;
    },
    cancelEdit() {
      this.editId = null;
    },
    saveOpeningBalance(balance) {
      this.isLoading = true;
      axios
        .post("/payment-method-balance/update-opening", {
          id: balance.id,
          opening_balance: this.editBalance
        })
        .then((response) => {
          this.isLoading = false;
          if (response.data.success) {
            this.editId = null;
            this.$notification.success("Payment method balance updated successfully.", {
              timer: 3,
              position: "bottomCenter",
            });
            this.search();
          }
        })
        .catch((error) => {
          this.isLoading = false;
          let msg = "Failed to update opening balance.";
          if (error.response && error.response.data && error.response.data.errors) {
            msg = Object.values(error.response.data.errors).flat().join(" ");
          } else if (error.response && error.response.data && error.response.data.message) {
            msg = error.response.data.message;
          }
          this.$notification.error(msg, {
            timer: 3,
            position: "bottomCenter",
          });
        });
    },
    finalizeYear() {
      this.$bvModal.hide('modal-1');
      this.isLoading = true;
      this.finalizeYearName = this.academicYearArr.find((year) => year.id == this.searchData.acd_year_id)?.text;
      axios
        .post("/payment-method-balance/finalize-year", {
          acd_year_id: this.searchData.acd_year_id
        })
        .then((response) => {
          this.isLoading = false;
          if (response.data.success) {
            this.$notification.success(`${this.finalizeYearName} finalized successfully.`, {
              timer: 3,
              position: "bottomCenter",
            });
            this.search();
          }
        })
        .catch((error) => {
          this.isLoading = false;
          let msg = "Failed to finalize academic year.";
          if (error.response && error.response.data && error.response.data.errors) {
            msg = Object.values(error.response.data.errors).flat().join(" ");
          } else if (error.response && error.response.data && error.response.data.message) {
            msg = error.response.data.message;
          }
          this.$notification.error(msg, {
            timer: 3,
            position: "bottomCenter",
          });
        })
        .finally(() => this.finalizeYearName = null);
    }
  },
  mounted() {
    this.getAcademicYear();
  }
};
